import { ChevronLeft, ChevronRight, Plus, RotateCcw, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { useViewMessages } from '@/lib/i18n';
import {
  type BookingRules,
  type BookingWeekday,
  type BookingWeeklyHours,
  type ScheduleConflict,
  type SchedulePreflight,
  useBlockedDates,
  useBookingRules,
  useBookingRulesPreflight,
  usePutBookingRules,
  useSetDateBlocked,
  useSetDayCapacity,
  useSetSlotOverride,
  useWeekGrid,
} from './data';

const MESSAGES = {
  nb: {
    title: 'Tilgjengelighet',
    sub: 'Styr hvilke tider som er åpne for prøvetime, og hvor mange plasser hver time har.',
    week: 'Uke',
    hours: 'Felles åpningstid',
    interval: 'Intervall',
    cap: 'Standard plasser per time',
    capHint: 'gjelder nye timer',
    days: 'Åpne dager',
    allWeeks: 'alle uker',
    weeklyTitle: 'Ukeplan per dag',
    weeklySub:
      'Sett åpningstiden for hver ukedag én gang. Når planen er i bruk, følger kalenderen under og bookingsiden disse tidene alle uker. Enkeltdager stenger du fortsatt med «Steng dag» i kalenderen.',
    weeklySubActive:
      'Gjelder alle uker. Kalenderen under og bookingsiden oppdateres så snart du lagrer. Enkeltdager stenger du med «Steng dag» i kalenderen.',
    weeklyDraft: 'Ikke i bruk – kundene ser felles åpningstid',
    weeklyLive: 'I bruk – styrer kalenderen og bookingsiden',
    saveWeekly: 'Lagre endringer',
    saveDraft: 'Lagre som utkast',
    undoWeekly: 'Angre',
    activateWeekly: 'Ta i bruk ukeplanen',
    deactivateWeekly: 'Gå tilbake til felles åpningstid',
    weeklyDraftSaved: 'Utkast lagret – ikke synlig for kunder ennå.',
    weeklySaved: 'Lagret. Kalenderen og bookingsiden følger de nye tidene.',
    weeklyActivated: 'Ukeplanen er i bruk. Kalenderen under følger nå tidene per dag.',
    weeklyDeactivated: 'Felles åpningstid er i bruk igjen. Ukeplanen er beholdt som utkast.',
    weeklySaveErr: 'Kunne ikke lagre ukeplanen',
    weeklyStarts: (n: number, last: string) =>
      `${n} starttid${n === 1 ? '' : 'er'} · siste ${last}`,
    weeklyNow: (label: string) => `Nå: ${label}`,
    gridDraftNote:
      'Kalenderen under viser tidene kundene kan booke nå (felles åpningstid). Ukeplanen gjelder først når du tar den i bruk.',
    confirmActivate: (changes: string) =>
      `Ta i bruk ukeplanen nå?\n\n${changes}\n\nKalenderen og bookingsiden bruker tidene per dag med en gang, for alle uker.`,
    confirmSaveActive: (changes: string) =>
      `Lagre endringene i ukeplanen?\n\n${changes}\n\nKalenderen og bookingsiden oppdateres med en gang, for alle uker.`,
    confirmDeactivate: (changes: string) =>
      `Gå tilbake til felles åpningstid?\n\n${changes}\n\nUkeplanen beholdes som utkast, og du kan ta den i bruk igjen senere.`,
    confirmRetained: (n: number, list: string) =>
      `${n === 1 ? '1 bekreftet booking' : `${n} bekreftede bookinger`} ligger utenfor åpningstiden etter denne endringen. ${n === 1 ? 'Den' : 'De'} beholdes og vises som «Utenfor åpningstid» i kalenderen:\n${list}`,
    moreRows: (n: number) => `… og ${n} til`,
    unsavedDiscarded: 'Ulagrede endringer i ukeplanen blir forkastet.',
    noChanges: 'Ingen endringer i tidene.',
    preflightClear: 'Sikkerhetssjekk bestått: ingen bekreftede bookinger havner over kapasiteten.',
    preflightBlocked: (n: number) =>
      `Kan ikke lagres: ${n === 1 ? '1 fremtidig booking' : `${n} fremtidige bookinger`} får ikke plass innenfor kapasiteten.`,
    preflightResolve:
      'Øk kapasiteten, reduser bufferen, eller flytt/avbryt bookingene under Bookinger. Prøv deretter igjen. En bekreftet kunde skal aldri havne over avtalt kapasitet, så det finnes ingen «lagre likevel».',
    preflightRetained: (n: number) =>
      `${n === 1 ? '1 bekreftet booking' : `${n} bekreftede bookinger`} ligger utenfor åpningstiden og vises som «Utenfor åpningstid». Ta kontakt med kunden om timen skal flyttes.`,
    reasonClosed: 'dagen er stengt',
    reasonBefore: 'før åpningstid',
    reasonAfter: 'etter siste gyldige starttid',
    reasonGrid: 'tiden følger ikke intervallet',
    reasonCapacity: 'for mange overlappende bookinger for kapasiteten/bufferen',
    outsideHours: 'Utenfor åpningstid',
    closed: 'Stengt',
    closedLower: 'stengt',
    legendOpen: 'Åpen',
    legendFull: 'Fullbooket',
    legendBlocked: 'Blokkert',
    bufferTitle: 'Buffer rundt hver time',
    bufferSub:
      'Prøvetimen tar {n} min. Legg på buffer så dere rekker å ta imot, melde inn og forberede neste.',
    before: 'Før timen',
    after: 'Etter timen',
    none: 'Ingen',
    buffer: 'Buffer',
    overlap: 'Overlapp',
    blocksPerBooking: 'Blokkerer {n} min per booking',
    footprint: '{b} min før · {s} min økt · {a} min etter',
    closeDay: 'Steng dag',
    openDay: 'Åpne dag',
    daySeats: 'Plasser/time hele dagen',
    fixedClosed: 'Stengt fast',
    blocked: 'Blokkert',
    booked: 'booket',
    bufferedOccupied: 'opptatt med buffer',
    anchorNoCapacity: 'Ingen ledig kapasitet · begrenset av overlappende time',
    anchorRemaining: (n: number) => `${n} ledig · begrenset av overlappende time`,
    min: 'min',
    seats: 'plasser',
    saved: 'Lagret',
    saveErr: 'Kunne ikke lagre',
    errCloseBeforeOpen: 'Stengetid må være etter åpningstid.',
    errSessionTooLong: 'Prøvetimen får ikke plass innenfor åpningstiden.',
    errIntervalTooShort: 'Intervallet må være ≥ prøvetime + buffer.',
    errFootprintNonPositive: 'Prøvetid pluss overlapp må være større enn 0 min.',
    errSessionTailNonPositive: 'Prøvetid pluss buffer etter start må være større enn 0 min.',
    errSessionHeadNonPositive: 'Prøvetid pluss overlapp før start må være større enn 0 min.',
    errMidnightStart: 'En bookingtid kan ikke starte ved midnatt.',
    confirmCloseDay: 'Stenge hele denne dagen for nye bookinger?',
    confirmOpenDay: 'Åpne denne dagen for booking igjen?',
    closeWarn: 'booking(er) er allerede booket denne dagen. De påvirkes ikke.',
    noDays: 'Ingen åpne dager denne uken.',
    loadFailed: 'Kunne ikke laste innstillinger.',
    prevWeek: 'Forrige uke',
    nextWeek: 'Neste uke',
    openSlot: (time: string) => `Åpne ${time}`,
    blockSlot: (time: string) => `Blokker ${time}`,
    openDate: (date: string) => `Åpne ${date}`,
    wd: ['SØNDAG', 'MANDAG', 'TIRSDAG', 'ONSDAG', 'TORSDAG', 'FREDAG', 'LØRDAG'],
    dshort: ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
    dlong: ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
    monShort: ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'],
    dateShort: (day: number, mon: string) => `${day}. ${mon}`,
  },
  en: {
    title: 'Availability',
    sub: 'Control which times are open for trials, and how many seats each hour has.',
    week: 'Week',
    hours: 'Shared opening hours',
    interval: 'Interval',
    cap: 'Default seats per hour',
    capHint: 'applies to new times',
    days: 'Open days',
    allWeeks: 'all weeks',
    weeklyTitle: 'Hours by weekday',
    weeklySub:
      'Set the opening hours for each weekday once. When the plan is in use, the calendar below and the booking page follow these hours every week. Single days are still closed with “Close day” in the calendar.',
    weeklySubActive:
      'Applies to every week. The calendar below and the booking page update as soon as you save. Close single days with “Close day” in the calendar.',
    weeklyDraft: 'Not in use – customers see the shared hours',
    weeklyLive: 'In use – controls the calendar and booking page',
    saveWeekly: 'Save changes',
    saveDraft: 'Save as draft',
    undoWeekly: 'Undo',
    activateWeekly: 'Use the weekly plan',
    deactivateWeekly: 'Return to shared hours',
    weeklyDraftSaved: 'Draft saved – not visible to customers yet.',
    weeklySaved: 'Saved. The calendar and booking page follow the new hours.',
    weeklyActivated: 'The weekly plan is in use. The calendar below now follows the per-day hours.',
    weeklyDeactivated: 'The shared hours are in use again. The weekly plan is kept as a draft.',
    weeklySaveErr: 'Could not save weekly plan',
    weeklyStarts: (n: number, last: string) =>
      `${n} start time${n === 1 ? '' : 's'} · last ${last}`,
    weeklyNow: (label: string) => `Now: ${label}`,
    gridDraftNote:
      'The calendar below shows the times customers can book now (shared hours). The weekly plan applies once you put it in use.',
    confirmActivate: (changes: string) =>
      `Put the weekly plan in use now?\n\n${changes}\n\nThe calendar and booking page use the per-day hours immediately, for every week.`,
    confirmSaveActive: (changes: string) =>
      `Save the changes to the weekly plan?\n\n${changes}\n\nThe calendar and booking page update immediately, for every week.`,
    confirmDeactivate: (changes: string) =>
      `Return to the shared hours?\n\n${changes}\n\nThe weekly plan is kept as a draft, and you can put it in use again later.`,
    confirmRetained: (n: number, list: string) =>
      `${n === 1 ? '1 confirmed booking falls' : `${n} confirmed bookings fall`} outside the opening hours after this change. ${n === 1 ? 'It is' : 'They are'} kept and shown as “Outside opening hours” in the calendar:\n${list}`,
    moreRows: (n: number) => `… and ${n} more`,
    unsavedDiscarded: 'Unsaved changes to the weekly plan are discarded.',
    noChanges: 'No changes to the hours.',
    preflightClear: 'Safety check passed: no confirmed booking ends up over capacity.',
    preflightBlocked: (n: number) =>
      `Cannot save: ${n === 1 ? '1 future booking does' : `${n} future bookings do`} not fit within the capacity.`,
    preflightResolve:
      'Increase the capacity, reduce the buffer, or move/cancel the bookings under Bookings. Then try again. A confirmed customer must never exceed the agreed capacity, so there is no “save anyway”.',
    preflightRetained: (n: number) =>
      `${n === 1 ? '1 confirmed booking falls' : `${n} confirmed bookings fall`} outside the opening hours and ${n === 1 ? 'is' : 'are'} shown as “Outside opening hours”. Contact the customer if the time should move.`,
    reasonClosed: 'the day is closed',
    reasonBefore: 'before opening',
    reasonAfter: 'after the last valid start',
    reasonGrid: 'the time is off the interval grid',
    reasonCapacity: 'too many overlapping bookings for the capacity/buffer',
    outsideHours: 'Outside opening hours',
    closed: 'Closed',
    closedLower: 'closed',
    legendOpen: 'Open',
    legendFull: 'Full',
    legendBlocked: 'Blocked',
    bufferTitle: 'Buffer around each time',
    bufferSub:
      'The trial takes {n} min. Add buffer so you can receive, register and prep the next.',
    before: 'Before',
    after: 'After',
    none: 'None',
    buffer: 'Buffer',
    overlap: 'Overlap',
    blocksPerBooking: 'Blocks {n} min per booking',
    footprint: '{b} min before · {s} min session · {a} min after',
    closeDay: 'Close day',
    openDay: 'Open day',
    daySeats: 'Seats/hour all day',
    fixedClosed: 'Closed',
    blocked: 'Blocked',
    booked: 'booked',
    bufferedOccupied: 'occupied with buffer',
    anchorNoCapacity: 'No capacity available · limited by an overlapping time',
    anchorRemaining: (n: number) => `${n} available · limited by an overlapping time`,
    min: 'min',
    seats: 'seats',
    saved: 'Saved',
    saveErr: 'Could not save',
    errCloseBeforeOpen: 'Closing must be after opening.',
    errSessionTooLong: 'The trial does not fit within opening hours.',
    errIntervalTooShort: 'Interval must be ≥ trial + buffer.',
    errFootprintNonPositive: 'Trial time plus overlap must be greater than 0 min.',
    errSessionTailNonPositive: 'Trial time plus buffer after the start must be greater than 0 min.',
    errSessionHeadNonPositive:
      'Trial time plus overlap before the start must be greater than 0 min.',
    errMidnightStart: 'A booking time cannot start at midnight.',
    confirmCloseDay: 'Close this whole day for new bookings?',
    confirmOpenDay: 'Re-open this day for booking?',
    closeWarn: 'booking(s) already exist that day. They are unaffected.',
    noDays: 'No open days this week.',
    loadFailed: 'Could not load settings.',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    openSlot: (time: string) => `Open ${time}`,
    blockSlot: (time: string) => `Block ${time}`,
    openDate: (date: string) => `Open ${date}`,
    wd: ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'],
    dshort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dlong: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    monShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    dateShort: (day: number, mon: string) => `${mon} ${day}`,
  },
};
type ViewMessages = (typeof MESSAGES)['nb'];

type Editable = Omit<BookingRules, 'id' | 'updatedAt' | 'weeklyHours' | 'weeklyHoursActive'>;
const WEEKDAY_KEYS: BookingWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const ALL_WEEKDAY_KEYS: BookingWeekday[] = [...WEEKDAY_KEYS, 'sun'];
const DAY_KEYS: (keyof Editable)[] = [
  'openMon',
  'openTue',
  'openWed',
  'openThu',
  'openFri',
  'openSat',
];
const FIELDS: (keyof Editable)[] = [
  ...DAY_KEYS,
  'openFromMin',
  'openToMin',
  'intervalMin',
  'defaultCapacity',
  'sessionLenMin',
  'bufferStartMin',
  'bufferEndMin',
  'horizonDays',
  'minNoticeHours',
];

// --- date helpers (UTC-noon anchored YYYY-MM-DD math) ---
function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function ymd(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
function parseYMD(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}
function addDays(s: string, n: number): string {
  const d = parseYMD(s);
  d.setUTCDate(d.getUTCDate() + n);
  return ymd(d);
}
function mondayOfToday(): string {
  const n = new Date();
  const d = new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()));
  const dow = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  return ymd(d);
}
function isoWeek(s: string): number {
  const d = parseYMD(s);
  const dn = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dn + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const fdn = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - fdn + 3);
  return 1 + Math.round((d.getTime() - firstThu.getTime()) / (7 * 86400000));
}
function dayNum(s: string, m: ViewMessages): string {
  const d = parseYMD(s);
  return m.dateShort(d.getUTCDate(), m.monShort[d.getUTCMonth()] ?? '');
}
function minToTime(min: number): string {
  return `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
}

type BufMode = 'none' | 'buffer' | 'overlap';
function bufMode(v: number): BufMode {
  return v > 0 ? 'buffer' : v < 0 ? 'overlap' : 'none';
}
function bufValue(mode: BufMode, min: number): number {
  return mode === 'none' ? 0 : mode === 'overlap' ? -Math.abs(min) : Math.abs(min);
}

export function AvailabilityPage() {
  const m = useViewMessages(MESSAGES);
  const rulesQuery = useBookingRules();
  if (rulesQuery.isLoading)
    return (
      <Frame m={m}>
        <LoadingState />
      </Frame>
    );
  if (rulesQuery.isError || !rulesQuery.data)
    return (
      <Frame m={m}>
        <ErrorState onRetry={() => rulesQuery.refetch()} />
      </Frame>
    );
  return <Editor m={m} rules={rulesQuery.data.rules} />;
}

function Frame({
  m,
  children,
  right,
}: {
  m: ViewMessages;
  children: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-title text-fg">{m.title}</h1>
          <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
        </div>
        {right}
      </header>
      {children}
    </div>
  );
}

function Editor({ m, rules }: { m: ViewMessages; rules: BookingRules }) {
  const put = usePutBookingRules();
  const [form, setForm] = useState<Editable>(() => pick(rules));
  const [weekStart, setWeekStart] = useState<string>(() => mondayOfToday());
  const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle');
  // The server explains WHY a schedule change is refused — «3 fremtidige
  // bookinger kommer i konflikt …» — and the contract client already puts that
  // text on the error. Throwing it away left the host staring at «Kunne ikke
  // lagre» with no way to know which bookings blocked it, or that the refusal
  // was protecting a real customer rather than a bug.
  const [saveErrMsg, setSaveErrMsg] = useState<string | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  const dirty = FIELDS.some((k) => form[k] !== (rules as unknown as Editable)[k]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reseed on server change, guarded by !dirty
  useEffect(() => {
    if (!dirty) setForm(pick(rules));
  }, [rules.updatedAt]);

  const validationError = validate(form, m);

  // Auto-save valid rule changes (no Save button — matches the design).
  const commit = (next: Editable) => {
    setForm(next);
    if (validate(next, m)) return; // invalid → keep editing, don't persist
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      put.mutate(next, {
        onSuccess: () => {
          setSaveErrMsg(null);
          setSaveState('saved');
          window.setTimeout(() => setSaveState('idle'), 1200);
        },
        onError: (err: unknown) => {
          setSaveErrMsg(err instanceof Error && err.message ? err.message : null);
          setSaveState('error');
        },
      });
    }, 350);
  };
  const setF = <K extends keyof Editable>(k: K, v: Editable[K]) => commit({ ...form, [k]: v });

  return (
    <Frame
      m={m}
      right={
        <WeekNav
          m={m}
          weekStart={weekStart}
          onPrev={() => setWeekStart((w) => addDays(w, -7))}
          onNext={() => setWeekStart((w) => addDays(w, 7))}
        />
      }
    >
      {/* ---- control bar ---- */}
      <section className="rounded-xl border border-border bg-surface p-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-4 sm:gap-x-8">
          {!rules.weeklyHoursActive ? (
            <Group label={m.hours}>
              <TimeSelect value={form.openFromMin} onChange={(v) => setF('openFromMin', v)} />
              <span className="text-fg-subtle">–</span>
              <TimeSelect value={form.openToMin} onChange={(v) => setF('openToMin', v)} />
            </Group>
          ) : null}
          <Group label={m.interval}>
            <Seg
              options={[20, 30, 60]}
              value={form.intervalMin}
              onChange={(v) => setF('intervalMin', v)}
              fmt={(v) => `${v} ${m.min}`}
            />
          </Group>
          <Group label={m.cap} hint={m.capHint}>
            <Seg
              options={[1, 2]}
              value={form.defaultCapacity}
              onChange={(v) => setF('defaultCapacity', v)}
            />
          </Group>
          <div className="flex flex-wrap items-center gap-4 text-label text-fg-muted sm:ml-auto">
            <Legend swatch="open" label={m.legendOpen} />
            <Legend swatch="full" label={m.legendFull} />
            <Legend swatch="blocked" label={m.legendBlocked} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!rules.weeklyHoursActive ? (
            <>
              <span className="mr-1 text-label font-medium text-fg-muted">{m.days}</span>
              {DAY_KEYS.map((k, i) => {
                const on = Boolean(form[k]);
                return (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setF(k, !on as Editable[typeof k])}
                    className={on ? segActive : segIdle}
                  >
                    {m.dshort[i]}
                  </button>
                );
              })}
              <span className="ml-1 text-label text-fg-subtle">{m.allWeeks}</span>
            </>
          ) : null}
          <span className="min-h-5 w-full text-label sm:ml-auto sm:w-auto">
            {validationError ? (
              <span className="text-bad-fg">{validationError}</span>
            ) : saveState === 'saved' ? (
              <span className="text-good-fg">{m.saved}</span>
            ) : saveState === 'error' ? (
              <span className="text-bad-fg">{saveErrMsg ?? m.saveErr}</span>
            ) : null}
          </span>
        </div>
      </section>

      {/* Per-weekday hours. Once in use they drive the grid below and the
          public widget for every week; until then the shared hours above do. */}
      <WeeklyHoursCard m={m} rules={rules} />

      {/* ---- buffer card ---- */}
      <BufferCard m={m} form={form} setF={setF} />

      {/* ---- week grid ---- */}
      {!rules.weeklyHoursActive ? (
        <p className="-mb-2 text-label text-fg-muted">{m.gridDraftNote}</p>
      ) : null}
      <WeekGrid m={m} weekStart={weekStart} defaultCap={form.defaultCapacity} />

      {/* ---- closed dates (kept as a quick list below the grid) ---- */}
      <BlockedDates m={m} />
    </Frame>
  );
}

function WeekNav({
  m,
  weekStart,
  onPrev,
  onNext,
}: {
  m: ViewMessages;
  weekStart: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  const end = addDays(weekStart, 5);
  return (
    <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
      <button
        type="button"
        aria-label={m.prevWeek}
        onClick={onPrev}
        className="grid size-10 place-items-center rounded-lg border border-border bg-surface text-fg hover:bg-surface-2"
      >
        <ChevronLeft className="size-5" />
      </button>
      <div className="min-w-[120px] rounded-lg border border-border bg-surface px-4 py-1.5 text-center sm:min-w-[150px]">
        <div className="text-micro font-semibold tracking-wider text-fg-subtle">
          {m.week} {isoWeek(weekStart)}
        </div>
        <div className="text-label font-medium text-fg">
          {dayNum(weekStart, m)} – {dayNum(end, m)}
        </div>
      </div>
      <button
        type="button"
        aria-label={m.nextWeek}
        onClick={onNext}
        className="grid size-10 place-items-center rounded-lg border border-border bg-surface text-fg hover:bg-surface-2"
      >
        <ChevronRight className="size-5" />
      </button>
    </div>
  );
}

function copyWeekly(hours: BookingWeeklyHours): BookingWeeklyHours {
  return {
    mon: { ...hours.mon },
    tue: { ...hours.tue },
    wed: { ...hours.wed },
    thu: { ...hours.thu },
    fri: { ...hours.fri },
    sat: { ...hours.sat },
    sun: { ...hours.sun, open: false },
  };
}

function sameDay(
  left: BookingWeeklyHours[BookingWeekday],
  right: BookingWeeklyHours[BookingWeekday],
) {
  // Hidden times of a closed day never reach a customer, so they are not a change.
  if (!left.open && !right.open) return true;
  return left.open === right.open && left.fromMin === right.fromMin && left.toMin === right.toMin;
}

function sameWeekly(left: BookingWeeklyHours, right: BookingWeeklyHours): boolean {
  return ALL_WEEKDAY_KEYS.every(
    (key) =>
      left[key].open === right[key].open &&
      left[key].fromMin === right[key].fromMin &&
      left[key].toMin === right[key].toMin,
  );
}

/** The hours customers can book right now: the weekly plan when it is in use,
 * otherwise the shared legacy hours on every open weekday. */
function liveHours(rules: BookingRules): BookingWeeklyHours {
  if (rules.weeklyHoursActive) return copyWeekly(rules.weeklyHours);
  const shared = (open: boolean) => ({ open, fromMin: rules.openFromMin, toMin: rules.openToMin });
  return {
    mon: shared(rules.openMon),
    tue: shared(rules.openTue),
    wed: shared(rules.openWed),
    thu: shared(rules.openThu),
    fri: shared(rules.openFri),
    sat: shared(rules.openSat),
    sun: shared(false),
  };
}

function dayLabel(day: BookingWeeklyHours[BookingWeekday], m: ViewMessages): string {
  return day.open ? `${minToTime(day.fromMin)}–${minToTime(day.toMin)}` : m.closedLower;
}

/** Mirrors the server's scheduledStartsForDay: a start fits when the session
 * plus the after-buffer ends by closing time. */
function dayStarts(
  day: BookingWeeklyHours[BookingWeekday],
  rules: BookingRules,
): { count: number; last: number | null } {
  if (!day.open || rules.intervalMin <= 0) return { count: 0, last: null };
  let count = 0;
  let last: number | null = null;
  for (
    let minutes = day.fromMin;
    minutes + rules.sessionLenMin + rules.bufferEndMin <= day.toMin;
    minutes += rules.intervalMin
  ) {
    count++;
    last = minutes;
  }
  return { count, last };
}

/** «Fredag: 10:00–19:00 → 10:00–14:00», one line per weekday that changes. */
function describeChanges(
  from: BookingWeeklyHours,
  to: BookingWeeklyHours,
  m: ViewMessages,
): string {
  const lines = WEEKDAY_KEYS.flatMap((key, index) =>
    sameDay(from[key], to[key])
      ? []
      : [`${m.dlong[index]}: ${dayLabel(from[key], m)} → ${dayLabel(to[key], m)}`],
  );
  return lines.length ? lines.join('\n') : m.noChanges;
}

/** A closed day keeps its last times so re-opening restores them. The server
 * still rejects impossible hidden times, so repair only those before sending. */
function sanitizeClosedDays(hours: BookingWeeklyHours): BookingWeeklyHours {
  const next = copyWeekly(hours);
  for (const key of ALL_WEEKDAY_KEYS) {
    const day = next[key];
    if (day.open || (day.fromMin >= 1 && day.toMin > day.fromMin)) continue;
    const fromMin = Math.min(Math.max(day.fromMin, 1), 1380);
    next[key] = { open: false, fromMin, toMin: Math.min(fromMin + 60, 1440) };
  }
  return next;
}

function validateWeekly(
  hours: BookingWeeklyHours,
  rules: BookingRules,
  m: ViewMessages,
): { key: BookingWeekday; message: string } | null {
  for (const [index, key] of WEEKDAY_KEYS.entries()) {
    const day = hours[key];
    if (!day.open) continue;
    const problem =
      day.fromMin < 1
        ? m.errMidnightStart
        : day.toMin <= day.fromMin
          ? m.errCloseBeforeOpen
          : rules.sessionLenMin + rules.bufferEndMin > day.toMin - day.fromMin
            ? m.errSessionTooLong
            : null;
    if (problem) return { key, message: `${m.dlong[index]}: ${problem}` };
  }
  return null;
}

function conflictReason(conflict: ScheduleConflict, m: ViewMessages): string {
  return conflict.reason === 'closed-day'
    ? m.reasonClosed
    : conflict.reason === 'before-hours'
      ? m.reasonBefore
      : conflict.reason === 'after-hours'
        ? m.reasonAfter
        : conflict.reason === 'over-capacity'
          ? m.reasonCapacity
          : m.reasonGrid;
}

function ConflictList({ conflicts, m }: { conflicts: ScheduleConflict[]; m: ViewMessages }) {
  return (
    <ul className="mt-2 grid gap-1.5 text-label text-fg sm:grid-cols-2">
      {conflicts.map((conflict) => (
        <li key={conflict.id} className="rounded-md bg-surface px-2.5 py-2">
          <span className="font-semibold">
            {conflict.localDate} · {conflict.localTime}
          </span>
          <span className="text-fg-muted">
            {' '}
            · {conflict.ref} · {conflict.name} · {conflictReason(conflict, m)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PreflightResult({ result, m }: { result: SchedulePreflight; m: ViewMessages }) {
  // The server refuses only capacity breaches; every other conflict is a booking
  // it keeps and labels «Utenfor åpningstid». Show the two very differently.
  const blocking = result.conflicts.filter((conflict) => conflict.reason === 'over-capacity');
  const retained = result.conflicts.filter((conflict) => conflict.reason !== 'over-capacity');
  return (
    <div className="mt-4 flex flex-col gap-2">
      {result.clear ? (
        <div className="rounded-lg bg-good-tint px-3 py-2.5 text-label font-medium text-good-fg">
          {m.preflightClear}
        </div>
      ) : (
        <div className="rounded-lg border border-bad/40 bg-bad-tint px-3 py-3">
          <div className="text-body font-semibold text-bad-fg">
            {result.validationError ??
              m.preflightBlocked(result.blockingConflicts ?? blocking.length)}
          </div>
          {blocking.length > 0 ? <ConflictList conflicts={blocking} m={m} /> : null}
          {result.validationError ? null : (
            <p className="mt-2 text-label text-bad-fg">{m.preflightResolve}</p>
          )}
        </div>
      )}
      {retained.length > 0 ? (
        <div className="rounded-lg border border-warn/40 bg-warn-tint px-3 py-3">
          <div className="text-label font-semibold text-warn-fg">
            {m.preflightRetained(retained.length)}
          </div>
          <ConflictList conflicts={retained} m={m} />
        </div>
      ) : null}
    </div>
  );
}

type WeeklyAction = 'draft' | 'activate' | 'save' | 'deactivate';

function WeeklyHoursCard({ m, rules }: { m: ViewMessages; rules: BookingRules }) {
  const put = usePutBookingRules();
  const preflight = useBookingRulesPreflight();
  const [hours, setHours] = useState<BookingWeeklyHours>(() => copyWeekly(rules.weeklyHours));
  const [status, setStatus] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);
  const dirty = !sameWeekly(hours, rules.weeklyHours);
  const validationError = validateWeekly(hours, rules, m);
  const live = liveHours(rules);
  const busy = put.isPending || preflight.isPending;

  // biome-ignore lint/correctness/useExhaustiveDependencies: only reseed after a successful/refetched server version
  useEffect(() => {
    if (!dirty) setHours(copyWeekly(rules.weeklyHours));
  }, [rules.updatedAt]);

  const updateDay = (key: BookingWeekday, patch: Partial<BookingWeeklyHours[BookingWeekday]>) => {
    setHours((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
      sun: { ...current.sun, open: false },
    }));
    setStatus(null);
    preflight.reset();
  };

  const successText: Record<WeeklyAction, string> = {
    draft: m.weeklyDraftSaved,
    activate: m.weeklyActivated,
    save: m.weeklySaved,
    deactivate: m.weeklyDeactivated,
  };

  const persist = (action: WeeklyAction, candidate: BookingWeeklyHours) => {
    // The server decides from ITS mode whether {weeklyHours} is a draft or a
    // live edit, so tell it which mode this screen showed. If another tab has
    // switched modes since, the write is refused instead of silently going live.
    const expectWeeklyHoursActive = action === 'save' || action === 'deactivate';
    const body =
      action === 'deactivate'
        ? { weeklyHoursActive: false, expectWeeklyHoursActive }
        : action === 'activate'
          ? { weeklyHours: candidate, weeklyHoursActive: true, expectWeeklyHoursActive }
          : { weeklyHours: candidate, expectWeeklyHoursActive };
    put.mutate(body, {
      onSuccess: (result) => {
        setHours(copyWeekly(result.rules.weeklyHours));
        setStatus({ tone: 'good', text: successText[action] });
      },
      onError: (err: unknown) => {
        setStatus({
          tone: 'bad',
          text: err instanceof Error && err.message ? err.message : m.weeklySaveErr,
        });
        // A booking may have landed after the read-only check. The locked write
        // refuses it; refresh the conflict list so staff see who is affected.
        if (action !== 'draft') {
          preflight.mutate(
            action === 'deactivate' ? { weeklyHoursActive: false } : { weeklyHours: candidate },
          );
        }
      },
    });
  };

  const run = (action: WeeklyAction) => {
    if (action !== 'deactivate' && validationError) return;
    setStatus(null);
    const candidate = sanitizeClosedDays(hours);
    // A draft never touches the customer calendar, so it needs no safety check.
    if (action === 'draft') {
      preflight.reset();
      persist(action, candidate);
      return;
    }
    preflight.mutate(
      action === 'deactivate' ? { weeklyHoursActive: false } : { weeklyHours: candidate },
      {
        onSuccess: (result) => {
          if (!result.clear) {
            const blocking = result.conflicts.filter(
              (conflict) => conflict.reason === 'over-capacity',
            ).length;
            setStatus({
              tone: 'bad',
              text:
                result.validationError ?? m.preflightBlocked(result.blockingConflicts ?? blocking),
            });
            return;
          }
          const retained = result.conflicts.filter(
            (conflict) => conflict.reason !== 'over-capacity',
          );
          const question =
            action === 'deactivate'
              ? m.confirmDeactivate(
                  describeChanges(live, liveHours({ ...rules, weeklyHoursActive: false }), m),
                ) + (dirty ? `\n\n${m.unsavedDiscarded}` : '')
              : action === 'activate'
                ? m.confirmActivate(describeChanges(live, candidate, m))
                : m.confirmSaveActive(describeChanges(live, candidate, m));
          // Name the customers, not just a count: the list below the card has
          // not rendered yet when the dialog opens.
          const rows = retained
            .slice(0, 5)
            .map((conflict) => `• ${conflict.localDate} ${conflict.localTime} · ${conflict.name}`);
          if (retained.length > 5) rows.push(m.moreRows(retained.length - 5));
          const message =
            retained.length > 0
              ? `${question}\n\n${m.confirmRetained(retained.length, rows.join('\n'))}`
              : question;
          if (window.confirm(message)) persist(action, candidate);
          else preflight.reset();
        },
        onError: (err: unknown) =>
          setStatus({
            tone: 'bad',
            text: err instanceof Error && err.message ? err.message : m.weeklySaveErr,
          }),
      },
    );
  };

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <div className="max-w-3xl">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-section font-medium text-fg">{m.weeklyTitle}</h2>
          <span
            className={
              rules.weeklyHoursActive
                ? 'rounded-full bg-good-tint px-2.5 py-1 text-micro font-semibold text-good-fg'
                : 'rounded-full bg-warn-tint px-2.5 py-1 text-micro font-semibold text-warn-fg'
            }
          >
            {rules.weeklyHoursActive ? m.weeklyLive : m.weeklyDraft}
          </span>
        </div>
        <p className="mt-1 text-label text-fg-muted">
          {rules.weeklyHoursActive ? m.weeklySubActive : m.weeklySub}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {WEEKDAY_KEYS.map((key, index) => {
          const day = hours[key];
          const starts = dayStarts(day, rules);
          const changed = !sameDay(day, live[key]);
          return (
            <div
              key={key}
              className={`flex flex-col gap-1.5 rounded-lg border bg-surface-2 px-3 py-2.5 ${
                validationError?.key === key
                  ? 'border-bad'
                  : changed
                    ? 'border-brand/60'
                    : 'border-border'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  aria-pressed={day.open}
                  disabled={busy}
                  onClick={() => updateDay(key, { open: !day.open })}
                  className={`${day.open ? segActive : segIdle} disabled:opacity-60`}
                >
                  {m.dshort[index]}
                </button>
                {day.open ? (
                  <div className="flex items-center gap-1.5">
                    <TimeSelect
                      value={day.fromMin}
                      disabled={busy}
                      onChange={(fromMin) => updateDay(key, { fromMin })}
                    />
                    <span className="text-fg-subtle">–</span>
                    <TimeSelect
                      value={day.toMin}
                      disabled={busy}
                      onChange={(toMin) => updateDay(key, { toMin })}
                    />
                  </div>
                ) : (
                  <span className="px-2 text-label text-fg-subtle">{m.closed}</span>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-x-3 text-label text-fg-muted">
                <span>
                  {starts.last !== null ? m.weeklyStarts(starts.count, minToTime(starts.last)) : ''}
                </span>
                {changed ? (
                  <span className="text-warn-fg">{m.weeklyNow(dayLabel(live[key], m))}</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {preflight.data ? <PreflightResult result={preflight.data} m={m} /> : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {rules.weeklyHoursActive ? (
          <>
            <Button
              size="sm"
              disabled={!dirty || Boolean(validationError) || busy}
              onClick={() => run('save')}
            >
              {m.saveWeekly}
            </Button>
            {dirty ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setHours(copyWeekly(rules.weeklyHours));
                  setStatus(null);
                  preflight.reset();
                }}
              >
                {m.undoWeekly}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              className="sm:ml-auto"
              disabled={busy}
              onClick={() => run('deactivate')}
            >
              {m.deactivateWeekly}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              disabled={Boolean(validationError) || busy}
              onClick={() => run('activate')}
            >
              {m.activateWeekly}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!dirty || Boolean(validationError) || busy}
              onClick={() => run('draft')}
            >
              {m.saveDraft}
            </Button>
          </>
        )}
        <span className="min-h-5 w-full text-label sm:w-auto" aria-live="polite">
          {validationError ? (
            <span className="text-bad-fg">{validationError.message}</span>
          ) : status ? (
            <span className={status.tone === 'good' ? 'text-good-fg' : 'text-bad-fg'}>
              {status.text}
            </span>
          ) : null}
        </span>
      </div>
    </section>
  );
}

function BufferCard({
  m,
  form,
  setF,
}: {
  m: ViewMessages;
  form: Editable;
  setF: <K extends keyof Editable>(k: K, v: Editable[K]) => void;
}) {
  const footprint = form.bufferStartMin + form.sessionLenMin + form.bufferEndMin;
  const minsOpts = [5, 10, 15, 20, 30];
  const renderBuf = (field: 'bufferStartMin' | 'bufferEndMin', label: string) => {
    const v = form[field];
    const mode = bufMode(v);
    const mins = Math.abs(v) || 5;
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-label font-medium text-fg-muted">{label}</span>
        <div className="flex items-center gap-2">
          <Seg<BufMode>
            options={['none', 'buffer', 'overlap']}
            value={mode}
            onChange={(nm) => setF(field, bufValue(nm, mins))}
            fmt={(o) => (o === 'none' ? m.none : o === 'buffer' ? m.buffer : m.overlap)}
          />
          <select
            disabled={mode === 'none'}
            value={mins}
            onChange={(e) => setF(field, bufValue(mode, Number(e.target.value)))}
            className="h-9 rounded-md border border-border bg-surface px-2 text-label text-fg disabled:opacity-40"
          >
            {minsOpts.map((o) => (
              <option key={o} value={o}>
                {o} {m.min}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };
  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-4 sm:gap-x-10">
        <div className="max-w-full sm:max-w-xs">
          <div className="text-micro font-semibold uppercase tracking-wider text-fg-subtle">
            {m.bufferTitle}
          </div>
          <p className="mt-1 text-label text-fg-muted">
            {m.bufferSub.replace('{n}', String(form.sessionLenMin))}
          </p>
        </div>
        {renderBuf('bufferStartMin', m.before)}
        {renderBuf('bufferEndMin', m.after)}
      </div>
      <div className="mt-4 inline-flex flex-col gap-0.5 rounded-lg bg-brand-tint px-4 py-2">
        <span className="text-body font-medium text-brand">
          {m.blocksPerBooking.replace('{n}', String(footprint))}
        </span>
        <span className="text-label text-brand/80">
          {m.footprint
            .replace('{b}', String(form.bufferStartMin))
            .replace('{s}', String(form.sessionLenMin))
            .replace('{a}', String(form.bufferEndMin))}
        </span>
      </div>
    </section>
  );
}

function WeekGrid({
  m,
  weekStart,
  defaultCap,
}: {
  m: ViewMessages;
  weekStart: string;
  defaultCap: number;
}) {
  const week = useWeekGrid(weekStart);
  const setSlot = useSetSlotOverride();
  const setDayCap = useSetDayCapacity();
  const setBlocked = useSetDateBlocked();

  if (week.isLoading) return <LoadingState />;
  if (week.isError || !week.data) return <ErrorState onRetry={() => week.refetch()} />;
  if (week.data.days.length === 0) return <p className="text-body text-fg-muted">{m.noDays}</p>;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {week.data.days.map((day) => (
        <div key={day.date} className="flex flex-col gap-2.5">
          <div
            className={`rounded-lg border px-3 py-2.5 ${day.closed ? 'border-border bg-surface-3/40' : 'border-border bg-surface-2'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-micro font-semibold tracking-wider text-fg-subtle">
                  {m.wd[day.weekday]}
                </div>
                <div
                  className={`text-section font-medium ${day.closed ? 'text-fg-subtle' : 'text-fg'}`}
                >
                  {dayNum(day.date, m)}
                </div>
              </div>
              {day.closed ? (
                <span className="rounded-md bg-surface-3 px-2.5 py-1 text-label font-medium text-fg-subtle">
                  {m.fixedClosed}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const next = !day.dayBlocked;
                    if (window.confirm(next ? m.confirmCloseDay : m.confirmOpenDay)) {
                      setBlocked.mutate(
                        { date: day.date, blocked: next },
                        {
                          onSuccess: (res) => {
                            if (next && res.existingBookings > 0)
                              window.alert(`${res.existingBookings} ${m.closeWarn}`);
                          },
                        },
                      );
                    }
                  }}
                  className={
                    day.dayBlocked
                      ? 'rounded-md border border-brand bg-brand-tint px-2.5 py-1.5 text-label font-medium text-brand sm:py-1'
                      : 'rounded-md border border-border bg-surface px-2.5 py-1.5 text-label text-fg-muted hover:bg-surface-2 sm:py-1'
                  }
                >
                  {day.dayBlocked ? m.openDay : m.closeDay}
                </button>
              )}
            </div>
            {!day.closed && !day.dayBlocked ? (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-label text-fg-muted">{m.daySeats}</span>
                <Seg
                  options={[1, 2]}
                  value={day.dayCapacity ?? 0}
                  onChange={(v) => setDayCap.mutate({ date: day.date, capacity: v })}
                  small
                />
              </div>
            ) : null}
          </div>

          {day.closed && day.slots.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-surface-2/30 px-3 py-8 text-center text-label text-fg-subtle">
              {m.fixedClosed}
            </div>
          ) : (
            day.slots.map((s) => {
              const full = !s.blocked && s.remaining <= 0;
              const anchorConstrained = s.remaining < Math.max(0, s.capacity - s.occupied);
              const cardCls = s.outsideHours
                ? 'rounded-lg border border-bad-fg/40 bg-bad-tint px-3 py-2.5'
                : s.blocked
                  ? 'rounded-lg border border-dashed border-border bg-surface-3/40 px-3 py-2.5'
                  : full
                    ? 'rounded-lg border border-brand bg-brand px-3 py-2.5'
                    : 'rounded-lg border border-border bg-surface px-3 py-2.5';
              const timeCls = s.outsideHours
                ? 'text-bad-fg'
                : full
                  ? 'text-brand-fg'
                  : s.blocked
                    ? 'text-fg-subtle'
                    : 'text-fg';
              const btnCls = full
                ? 'border-brand-fg/30 text-brand-fg'
                : s.blocked
                  ? 'border-border text-brand'
                  : 'border-border text-fg-subtle hover:text-bad-fg';
              return (
                <div key={s.startsAt} className={cardCls}>
                  <div className="flex items-center justify-between">
                    <span className={`text-section font-medium ${timeCls}`}>{s.time}</span>
                    {!s.outsideHours ? (
                      <button
                        type="button"
                        aria-label={s.blocked ? m.openSlot(s.time) : m.blockSlot(s.time)}
                        aria-pressed={s.blocked}
                        title={s.blocked ? m.openDay : m.blocked}
                        onClick={() =>
                          setSlot.mutate({
                            slotStartsAt: s.startsAt,
                            capacity: s.capacity,
                            blocked: !s.blocked,
                          })
                        }
                        className={`grid size-9 place-items-center rounded-md border sm:size-7 ${btnCls}`}
                      >
                        {s.blocked ? (
                          <RotateCcw className="size-3.5" />
                        ) : (
                          <X className="size-3.5" />
                        )}
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                    <span
                      className={
                        s.outsideHours
                          ? 'text-label font-semibold text-bad-fg'
                          : s.blocked
                            ? 'text-label font-medium text-fg-subtle'
                            : full
                              ? 'text-label font-medium text-brand-fg'
                              : s.occupied > 0
                                ? 'text-label text-accent-lead'
                                : 'text-label text-fg-muted'
                      }
                    >
                      {s.outsideHours
                        ? `${m.outsideHours} · ${s.booked} ${m.booked}`
                        : s.blocked
                          ? m.blocked
                          : anchorConstrained
                            ? s.remaining === 0
                              ? m.anchorNoCapacity
                              : m.anchorRemaining(s.remaining)
                            : s.occupied !== s.booked
                              ? `${s.booked} ${m.booked} · ${s.occupied}/${s.capacity} ${m.bufferedOccupied}`
                              : `${s.booked}/${s.capacity} ${m.booked}`}
                    </span>
                    {!s.blocked && !s.outsideHours ? (
                      <Seg
                        options={[1, 2]}
                        value={s.capacity}
                        onChange={(v) =>
                          setSlot.mutate({
                            slotStartsAt: s.startsAt,
                            capacity: v === defaultCap ? null : v,
                            blocked: false,
                          })
                        }
                        small
                      />
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ))}
    </div>
  );
}

function BlockedDates({ m }: { m: ViewMessages }) {
  const query = useBlockedDates();
  const setBlocked = useSetDateBlocked();
  const [newDate, setNewDate] = useState('');
  if (!query.data || query.data.dates.length === 0) {
    // Still offer a quick "close a date" entry even when none are closed yet.
  }
  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-body text-fg sm:w-auto"
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!newDate || setBlocked.isPending}
          onClick={() =>
            setBlocked.mutate(
              { date: newDate, blocked: true },
              {
                onSuccess: (res) => {
                  setNewDate('');
                  if (res.existingBookings > 0)
                    window.alert(`${res.existingBookings} ${m.closeWarn}`);
                },
              },
            )
          }
        >
          <Plus className="size-4" aria-hidden /> {m.closeDay}
        </Button>
        {query.data && query.data.dates.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {query.data.dates.map((d) => (
              <li
                key={d.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-2 px-3 py-1 text-label text-fg"
              >
                {d.date}
                <button
                  type="button"
                  aria-label={m.openDate(d.date)}
                  className="-m-1 grid size-7 place-items-center text-fg-subtle hover:text-bad-fg sm:m-0 sm:size-auto"
                  onClick={() => {
                    if (window.confirm(m.confirmOpenDay))
                      setBlocked.mutate({ date: d.date, blocked: false });
                  }}
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

// --- small UI atoms ---
const segActive = 'rounded-md bg-brand px-3 py-1.5 text-label font-medium text-brand-fg';
const segIdle =
  'rounded-md border border-border bg-surface px-3 py-1.5 text-label text-fg-muted hover:bg-surface-2';

function Seg<T extends string | number>({
  options,
  value,
  onChange,
  fmt,
  small,
}: {
  options: T[];
  value: T;
  onChange: (v: T) => void;
  fmt?: (v: T) => string;
  small?: boolean;
}) {
  return (
    <div className="inline-flex gap-1 rounded-lg bg-surface-2 p-0.5">
      {options.map((o) => {
        const on = o === value;
        return (
          <button
            key={String(o)}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o)}
            className={
              (on ? 'bg-brand text-brand-fg' : 'text-fg-muted hover:text-fg') +
              (small
                ? ' rounded-md px-2 py-1.5 text-label font-medium sm:py-0.5'
                : ' rounded-md px-3 py-2 text-label font-medium sm:py-1')
            }
          >
            {fmt ? fmt(o) : o}
          </button>
        );
      })}
    </div>
  );
}

function TimeSelect({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const opts: number[] = [];
  for (let min = 7 * 60; min <= 21 * 60; min += 30) opts.push(min);
  if (!opts.includes(value)) opts.push(value);
  opts.sort((a, b) => a - b);
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-9 rounded-md border border-border bg-surface px-2 text-body text-fg disabled:opacity-60"
    >
      {opts.map((o) => (
        <option key={o} value={o}>
          {minToTime(o)}
        </option>
      ))}
    </select>
  );
}

function Group({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-micro font-semibold uppercase tracking-wider text-fg-subtle">
        {label}
        {hint ? (
          <span className="ml-1 normal-case font-normal tracking-normal text-fg-subtle">
            · {hint}
          </span>
        ) : null}
      </span>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

function Legend({ swatch, label }: { swatch: 'open' | 'full' | 'blocked'; label: string }) {
  const cls =
    swatch === 'open'
      ? 'border border-border bg-surface'
      : swatch === 'full'
        ? 'bg-brand'
        : 'border border-dashed border-border bg-surface-2';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`size-3 rounded-sm ${cls}`} aria-hidden />
      {label}
    </span>
  );
}

function validate(f: Editable, m: ViewMessages): string | null {
  if (f.bufferStartMin + f.sessionLenMin + f.bufferEndMin <= 0) return m.errFootprintNonPositive;
  if (f.sessionLenMin + f.bufferEndMin <= 0) return m.errSessionTailNonPositive;
  if (f.sessionLenMin + f.bufferStartMin <= 0) return m.errSessionHeadNonPositive;
  if (f.openFromMin < 1) return m.errMidnightStart;
  if (f.openToMin <= f.openFromMin) return m.errCloseBeforeOpen;
  if (f.sessionLenMin + f.bufferEndMin > f.openToMin - f.openFromMin) return m.errSessionTooLong;
  // interval may be < footprint on purpose (overlap model) — not an error.
  return null;
}

function pick(r: BookingRules): Editable {
  const out = {} as Editable;
  for (const k of FIELDS)
    (out as Record<string, unknown>)[k] = (r as unknown as Record<string, unknown>)[k];
  return out;
}
